import { Menu, MenuItem, SubMenu } from "@szhsin/react-menu";
import { compact, isEqual } from "lodash";
import moment from "moment";
import { useCallback, useMemo } from "react";
import {
  AssessmentType,
  Maybe,
  PerformanceAssessmentAnswerFilter,
  TalentIndicatorAssessmentQuestionNode,
  TalentIndicatorType,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import {
  CareerTrackPickerDropdown,
  CareerTrackPickerTrack,
} from "@apps/org-settings/components/competencies/career-track-picker";
import {
  CareerTrackRolePickerDropdown,
  CareerTrackRolePickerRole,
} from "@apps/org-settings/components/competencies/career-track-role-picker";
import useLabel, { UseLabelType } from "@apps/use-label/use-label";
import Button, { buttonTheme } from "@components/button/button";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import InputDatePicker from "@components/input/input-datepicker";
import RecipientDropdown from "@components/people-dropdown/recipient-dropdown";
import {
  TopicflowIcon,
  TopicflowIconMap,
  getTopicflowIcon,
} from "@components/topicflow-icon/topicflow-icon";
import UserCombobox from "@components/user-combobox/user-combobox";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import { CareerTrackLevelDropdown } from "../../career-level-picker";
import { TeamPickerDropdown } from "../../team-picker";
import { hasFilterType, isFilterEqual, updateFilters } from "./helpers";
import {
  AssessmentReportFilterItem,
  AssessmentReportFilterType,
} from "./types";

const filterTypeToTitle = (
  label: UseLabelType
): { [key in AssessmentReportFilterType]: string } => ({
  [AssessmentReportFilterType.TEAM]: label("team", {
    capitalize: true,
    pluralize: true,
  }),
  [AssessmentReportFilterType.ROLE]: "Roles",
  [AssessmentReportFilterType.CAREER_TRACK]: "Career Tracks",
  [AssessmentReportFilterType.CAREER_TRACK_LEVEL]: "Role Levels",
  [AssessmentReportFilterType.USER]: "Employees",
  [AssessmentReportFilterType.REPORTS_TO]: "Reports To",
  [AssessmentReportFilterType.ANSWER]: "Answers",
  [AssessmentReportFilterType.TALENT_INDICATOR]: "Talent Indicators",
  [AssessmentReportFilterType.ENROLLMENT_DATE]: "Enrollment Date",
});

const Filter = ({
  filter,
  onChange,
  onRemove,
}: {
  filter: AssessmentReportFilterItem;
  onChange: (
    filterItems:
      | number[]
      | (string | null)[]
      | BasicUser[]
      | CareerTrackRolePickerRole[]
      | CareerTrackPickerTrack[]
      | PerformanceAssessmentAnswerFilter[]
  ) => void;
  onRemove: () => void;
}) => {
  const label = useLabel();

  const addButton = useMemo(() => {
    switch (filter.type) {
      case AssessmentReportFilterType.TEAM:
        return (
          <TeamPickerDropdown
            teams={filter.data}
            onChange={(team) => onChange([...filter.data, team])}
          >
            <ComboboxGeneric.Button
              icon={getTopicflowIcon("plus")}
              theme={buttonTheme.iconGray}
            />
          </TeamPickerDropdown>
        );
      case AssessmentReportFilterType.USER:
        return (
          <RecipientDropdown
            showCurrentUser
            recipients={filter.data}
            onChangeRecipient={(user) => onChange([...filter.data, user])}
          >
            <UserCombobox.Button
              icon={getTopicflowIcon("plus")}
              theme={buttonTheme.iconGray}
            />
          </RecipientDropdown>
        );
      case AssessmentReportFilterType.REPORTS_TO:
        return (
          <RecipientDropdown
            showCurrentUser
            recipients={filter.data}
            onChangeRecipient={(user) => onChange([...filter.data, user])}
          >
            <UserCombobox.Button
              icon={getTopicflowIcon("plus")}
              theme={buttonTheme.iconGray}
            />
          </RecipientDropdown>
        );
      case AssessmentReportFilterType.ROLE:
        return (
          <CareerTrackRolePickerDropdown
            roles={filter.data}
            onChange={(role) => onChange([...filter.data, role])}
          >
            <ComboboxGeneric.Button
              icon={getTopicflowIcon("plus")}
              theme={buttonTheme.iconGray}
            />
          </CareerTrackRolePickerDropdown>
        );
      case AssessmentReportFilterType.CAREER_TRACK_LEVEL:
        return (
          <CareerTrackLevelDropdown
            levels={filter.data}
            onChange={(level) => onChange([...filter.data, level])}
          >
            <ComboboxGeneric.Button
              icon={getTopicflowIcon("plus")}
              theme={buttonTheme.iconGray}
            />
          </CareerTrackLevelDropdown>
        );
      case AssessmentReportFilterType.CAREER_TRACK:
        return (
          <CareerTrackPickerDropdown
            careerTracks={filter.data}
            onChange={(careerTrack) => onChange([...filter.data, careerTrack])}
          >
            <ComboboxGeneric.Button
              icon={getTopicflowIcon("plus")}
              theme={buttonTheme.iconGray}
            />
          </CareerTrackPickerDropdown>
        );
      case AssessmentReportFilterType.TALENT_INDICATOR:
        return (
          <Menu
            className="text-sm z-dropdown"
            portal
            transition={false}
            menuButton={
              <button className="h-[28px] w-[28px] flex items-center justify-center font-medium rounded-md text-gray-600 hover:bg-gray-100">
                <TopicflowIcon type="plus" className="h-5 w-5" />
              </button>
            }
          >
            {filter.options.talentIndicator &&
              filter.options.talentIndicator.questionLabels.map(
                (opt, index) => {
                  const newFilterData = {
                    questionId: assertNonNull(
                      filter.options.talentIndicator?.questionId
                    ),
                    integerAnswer:
                      index +
                      assertNonNull(filter.options.talentIndicator?.startValue),
                  };
                  return (
                    <MenuItem
                      className="text-sm block"
                      key={opt}
                      onClick={() =>
                        onChange([
                          ...filter.data.filter(
                            (d) => !isEqual(d, newFilterData)
                          ),
                          {
                            questionId: assertNonNull(
                              filter.options.talentIndicator?.questionId
                            ),
                            integerAnswer:
                              index +
                              assertNonNull(
                                filter.options.talentIndicator?.startValue
                              ),
                          },
                        ])
                      }
                    >
                      {opt}
                    </MenuItem>
                  );
                }
              )}
          </Menu>
        );
      default:
        return null;
    }
  }, [filter, onChange]);

  const handleRemoveItem = useCallback(
    (id: number) => {
      switch (filter.type) {
        case AssessmentReportFilterType.TEAM:
          onChange(filter.data.filter((team) => team.id !== id));
          return;
        case AssessmentReportFilterType.USER:
          onChange(filter.data.filter((user) => user.id !== id));
          return;
        case AssessmentReportFilterType.REPORTS_TO:
          onChange(filter.data.filter((manager) => manager.id !== id));
          return;
        case AssessmentReportFilterType.ROLE:
          onChange(filter.data.filter((role) => role.id !== id));
          return;
        case AssessmentReportFilterType.CAREER_TRACK:
          onChange(filter.data.filter((careerTrack) => careerTrack.id !== id));
          return;
        case AssessmentReportFilterType.CAREER_TRACK_LEVEL:
          onChange(filter.data.filter((level) => level !== id));
          return;
        case AssessmentReportFilterType.TALENT_INDICATOR:
          onChange(
            filter.data.filter(
              (talentIndicator) => talentIndicator.integerAnswer !== id
            )
          );
      }
    },
    [filter, onChange]
  );

  const dataItemsContent = useMemo(() => {
    let dataItems: { label: string; id: number }[] = [];
    switch (filter.type) {
      case AssessmentReportFilterType.TEAM:
        dataItems = filter.data.map((team) => ({
          label: team.title,
          id: team.id,
        }));
        break;
      case AssessmentReportFilterType.USER:
        dataItems = filter.data.map((user) => ({
          label: user.name,
          id: user.id,
        }));
        break;
      case AssessmentReportFilterType.REPORTS_TO:
        dataItems = filter.data.map((manager) => ({
          label: manager.name,
          id: manager.id,
        }));
        break;
      case AssessmentReportFilterType.ROLE:
        dataItems = filter.data.map((role) => ({
          label: role.title,
          id: role.id,
        }));
        break;
      case AssessmentReportFilterType.CAREER_TRACK:
        dataItems = filter.data.map((track) => ({
          label: track.title,
          id: track.id,
        }));
        break;
      case AssessmentReportFilterType.CAREER_TRACK_LEVEL:
        dataItems = filter.data.map((level) => ({
          label: `Level ${level}`,
          id: level,
        }));
        break;
      case AssessmentReportFilterType.TALENT_INDICATOR:
        dataItems = filter.data.map((answer) => ({
          label:
            filter.options.talentIndicator?.questionLabels[
              answer.integerAnswer - filter.options.talentIndicator.startValue
            ] ?? `${answer.integerAnswer}`,
          id: answer.integerAnswer,
        }));
        break;
    }

    if (filter.type === AssessmentReportFilterType.ENROLLMENT_DATE) {
      return (
        <div className="mt-2 flex gap-2 items-center">
          <InputDatePicker
            showIcon
            isClearable
            wrapperClassName="flex-1"
            placeholderText="From"
            date={filter.data[0] ? moment(filter.data[0]).toDate() : null}
            onChangeDate={(date) =>
              onChange([
                date !== null ? moment(date).format("YYYY-MM-DD") : null,
                filter.data[1],
              ])
            }
            dateFormat="MMM d, yyyy"
            className="px-4 py-2 block w-full sm:text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
          <TopicflowIcon type="rightArrow" className="text-gray-500 w-4 h-4" />
          <InputDatePicker
            showIcon
            isClearable
            wrapperClassName="flex-1"
            placeholderText="To"
            date={filter.data[1] ? moment(filter.data[1]).toDate() : null}
            onChangeDate={(date) =>
              onChange([
                filter.data[0],
                date !== null ? moment(date).format("YYYY-MM-DD") : null,
              ])
            }
            dateFormat="MMM d, yyyy"
            className="px-4 py-2 block w-full sm:text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      );
    }

    return dataItems.length === 0 ? (
      <div className="mt-2 text-gray-400">No items added</div>
    ) : (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {dataItems.map((item) => (
          <div
            className="border rounded-lg px-2 py-1 text-sm flex items-center gap-1"
            key={item.label}
          >
            {item.label}
            <button
              className="ml-0.5 rounded-full p-0.5 border bg-gray-50 text-gray-600 hover:text-white hover:bg-blue-600 hover:border-blue-600"
              onClick={() => handleRemoveItem(item.id)}
            >
              <TopicflowIcon type="close" className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    );
  }, [filter, handleRemoveItem, onChange]);

  return (
    <div className="p-4 border rounded-md">
      <div className="flex items-center justify-between">
        <div className="font-medium">
          {filter.options.talentIndicator
            ? filter.options.talentIndicator.label
            : filterTypeToTitle(label)[filter.type]}
        </div>
        <div className="flex items-center gap-2">
          {addButton}
          <Button
            className="text-gray-600"
            icon={TopicflowIconMap.trash}
            theme={buttonTheme.iconGray}
            onClick={onRemove}
          />
        </div>
      </div>
      {dataItemsContent}
    </div>
  );
};

type FilterSelectOption = {
  label: string;
  type: AssessmentReportFilterType;
  talentIndicatorType?: TalentIndicatorType;
  questionId?: number;
  questionLabels?: string[];
  startValue?: number;
  submenu?: FilterSelectOption[];
};

const FilterMenuItem = ({
  option,
  filters,
  onUpdateFilters,
}: {
  option: FilterSelectOption;
  filters: AssessmentReportFilterItem[];
  onUpdateFilters: (filterData: AssessmentReportFilterItem[]) => void;
}) => {
  return (
    <MenuItem
      className="text-sm block"
      onClick={() => {
        const newFilter: AssessmentReportFilterItem = {
          type: option.type,
          data: [],
          options: {},
        };
        if (option.type === AssessmentReportFilterType.ENROLLMENT_DATE) {
          newFilter.data = [null, null];
        } else if (
          option.type === AssessmentReportFilterType.TALENT_INDICATOR
        ) {
          newFilter.options = {
            talentIndicator: {
              type: assertNonNull(option.talentIndicatorType),
              label: option.label,
              questionId: assertNonNull(option.questionId),
              questionLabels: assertNonNull(option.questionLabels),
              startValue: assertNonNull(option.startValue),
            },
          };
        }
        onUpdateFilters([...filters, newFilter]);
      }}
    >
      {option.label}
    </MenuItem>
  );
};

const AssessmentReportFilterContainer = ({
  assessmentType,
  isOngoingProgram,
  filters,
  talentIndicatorQuestions,
  onUpdateFilters,
}: {
  assessmentType: AssessmentType;
  isOngoingProgram: boolean;
  filters: AssessmentReportFilterItem[];
  talentIndicatorQuestions: (Pick<
    TalentIndicatorAssessmentQuestionNode,
    "id" | "startValue" | "endValue" | "labels"
  > & {
    talentIndicator?: Maybe<{
      id: number;
      name: string;
      customName?: Maybe<string>;
      talentIndicatorType: TalentIndicatorType;
    }>;
  })[];
  onUpdateFilters: (filterData: AssessmentReportFilterItem[]) => void;
}) => {
  const label = useLabel();

  const unusedTalentIndicatorQuestions = useMemo(() => {
    return talentIndicatorQuestions.filter((question) => {
      return !hasFilterType(
        filters,
        AssessmentReportFilterType.TALENT_INDICATOR,
        {
          talentIndicator: {
            type: assertNonNull(question.talentIndicator?.talentIndicatorType),
            questionId: question.id,
            questionLabels: [...question.labels.map(assertNonNull)],
            startValue: question.startValue,
            label:
              question.talentIndicator?.customName &&
              question.talentIndicator?.customName.trim() !== ""
                ? question.talentIndicator?.customName
                : assertNonNull(question.talentIndicator?.name),
          },
        }
      );
    });
  }, [filters, talentIndicatorQuestions]);

  const addFilterOptions: FilterSelectOption[] = useMemo(
    () =>
      compact([
        !hasFilterType(filters, AssessmentReportFilterType.USER) && {
          label: filterTypeToTitle(label)[AssessmentReportFilterType.USER],
          type: AssessmentReportFilterType.USER,
        },
        !hasFilterType(filters, AssessmentReportFilterType.REPORTS_TO) && {
          label:
            filterTypeToTitle(label)[AssessmentReportFilterType.REPORTS_TO],
          type: AssessmentReportFilterType.REPORTS_TO,
        },
        !hasFilterType(filters, AssessmentReportFilterType.TEAM) && {
          label: filterTypeToTitle(label)[AssessmentReportFilterType.TEAM],
          type: AssessmentReportFilterType.TEAM,
        },
        assessmentType === AssessmentType.Performance &&
          !hasFilterType(filters, AssessmentReportFilterType.ROLE) && {
            label: filterTypeToTitle(label)[AssessmentReportFilterType.ROLE],
            type: AssessmentReportFilterType.ROLE,
          },
        assessmentType === AssessmentType.Performance &&
          !hasFilterType(
            filters,
            AssessmentReportFilterType.CAREER_TRACK_LEVEL
          ) && {
            label:
              filterTypeToTitle(label)[
                AssessmentReportFilterType.CAREER_TRACK_LEVEL
              ],
            type: AssessmentReportFilterType.CAREER_TRACK_LEVEL,
          },
        assessmentType === AssessmentType.Performance &&
          !hasFilterType(filters, AssessmentReportFilterType.CAREER_TRACK) && {
            label:
              filterTypeToTitle(label)[AssessmentReportFilterType.CAREER_TRACK],
            type: AssessmentReportFilterType.CAREER_TRACK,
          },
        assessmentType === AssessmentType.Performance &&
          isOngoingProgram &&
          !hasFilterType(filters, AssessmentReportFilterType.CAREER_TRACK) && {
            label:
              filterTypeToTitle(label)[
                AssessmentReportFilterType.ENROLLMENT_DATE
              ],
            type: AssessmentReportFilterType.ENROLLMENT_DATE,
          },
        assessmentType === AssessmentType.Performance &&
          unusedTalentIndicatorQuestions.length > 0 && {
            label:
              filterTypeToTitle(label)[
                AssessmentReportFilterType.TALENT_INDICATOR
              ],
            type: AssessmentReportFilterType.TALENT_INDICATOR,
            submenu: unusedTalentIndicatorQuestions.map((question) => {
              return {
                label:
                  question.talentIndicator?.customName &&
                  question.talentIndicator?.customName.trim() !== ""
                    ? question.talentIndicator?.customName
                    : assertNonNull(question.talentIndicator?.name),
                type: AssessmentReportFilterType.TALENT_INDICATOR,
                talentIndicatorType:
                  question.talentIndicator?.talentIndicatorType,
                questionId: question.id,
                questionLabels: [...question.labels.map(assertNonNull)],
                startValue: question.startValue,
              };
            }),
          },
      ]),
    [
      assessmentType,
      filters,
      label,
      unusedTalentIndicatorQuestions,
      isOngoingProgram,
    ]
  );

  const disabled = addFilterOptions.length === 0;
  return (
    <div className="flex flex-col gap-4">
      {filters
        .filter((item) => item.type !== AssessmentReportFilterType.ANSWER)
        .map((item) => {
          return (
            <Filter
              key={item.type}
              filter={item}
              onChange={(data) =>
                onUpdateFilters(
                  updateFilters(filters, item.type, data, item.options)
                )
              }
              onRemove={() =>
                onUpdateFilters(filters.filter((f) => !isFilterEqual(f, item)))
              }
            />
          );
        })}

      <Menu
        className="text-sm z-dropdown"
        portal
        transition={false}
        menuButton={
          <button
            disabled={disabled}
            className={classNames(
              "w-fit -ml-2 px-2 py-1 text-left text-sm font-medium rounded flex items-center gap-1.5 text-gray-600 hover:bg-gray-100",
              disabled && "opacity-50"
            )}
          >
            <TopicflowIcon type="plus" className="h-4 w-4" />
            Add Filter
          </button>
        }
      >
        {addFilterOptions.map((option) => {
          if (option.submenu) {
            return (
              <SubMenu label={option.label} key={option.label}>
                {option.submenu.map((subMenuOption) => (
                  <FilterMenuItem
                    key={subMenuOption.label}
                    option={subMenuOption}
                    filters={filters}
                    onUpdateFilters={onUpdateFilters}
                  />
                ))}
              </SubMenu>
            );
          }
          return (
            <FilterMenuItem
              key={option.label}
              option={option}
              filters={filters}
              onUpdateFilters={onUpdateFilters}
            />
          );
        })}
      </Menu>
    </div>
  );
};

export default AssessmentReportFilterContainer;
